import React from 'react';
import { Terminal, Sparkles, Bot } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWorkflowEditor } from './WorkflowEditorContext.shared';
import { WorkflowPropertiesTab } from './WorkflowPropertiesTab';
import { WorkflowVariablesTab } from './WorkflowVariablesTab';
import { WorkflowExecutionTab } from './WorkflowExecutionTab';
import { WorkflowAIChatTab } from './WorkflowAIChatTab';
import { WorkflowModificationLogTab } from './WorkflowModificationLogTab';

export const WorkflowSidebar: React.FC = () => {
  const {
    rightSidebarTab,
    setRightSidebarTab,
    selectedNodes,
    selectedNode,
    selectedDeviceId,
    workflow,
  } = useWorkflowEditor();

  const isAgentWorkflow = workflow?.type === 'agent';

  return (
    <div className="h-full bg-background overflow-hidden flex flex-col">
      {/* Tab Navigation */}
      <div className="border-b flex-shrink-0">
        <div className="flex">
          <button
            className={cn(
              'flex-1 px-2 py-3 text-xs font-medium border-b-2 transition-colors',
              rightSidebarTab === 'properties'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setRightSidebarTab('properties')}
          >
            Properties
          </button>
          <button
            className={cn(
              'flex-1 px-2 py-3 text-xs font-medium border-b-2 transition-colors',
              rightSidebarTab === 'variables'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setRightSidebarTab('variables')}
          >
            Variables
          </button>
          <button
            className={cn(
              'flex-1 px-2 py-3 text-xs font-medium border-b-2 transition-colors',
              rightSidebarTab === 'execution'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setRightSidebarTab('execution')}
          >
            <div className="flex items-center justify-center gap-1">
              <Terminal className="w-3 h-3" />
              Log
            </div>
          </button>
          {isAgentWorkflow && (
            <button
              className={cn(
                'flex-1 px-2 py-3 text-xs font-medium border-b-2 transition-colors',
                rightSidebarTab === 'modification-log'
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              )}
              onClick={() => setRightSidebarTab('modification-log')}
            >
              <div className="flex items-center justify-center gap-1">
                <Bot className="w-3 h-3" />
                Agent
              </div>
            </button>
          )}
          <button
            className={cn(
              'flex-1 px-2 py-3 text-xs font-medium border-b-2 transition-colors',
              rightSidebarTab === 'ai-chat'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            )}
            onClick={() => setRightSidebarTab('ai-chat')}
          >
            <div className="flex items-center justify-center gap-1">
              <Sparkles className="w-3 h-3" />
              AI
            </div>
          </button>
        </div>
      </div>

      {/* Tab Description */}
      {rightSidebarTab !== 'ai-chat' && rightSidebarTab !== 'modification-log' && (
        <div className="p-4 border-b flex-shrink-0">
          <h3 className="font-semibold text-sm">
            {rightSidebarTab === 'properties' ? 'Properties' :
             rightSidebarTab === 'variables' ? 'Workflow Variables' : 'Execution Log'}
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            {rightSidebarTab === 'properties'
              ? selectedNodes.length > 1
                ? `${selectedNodes.length} nodes selected`
                : selectedNode
                  ? 'Configure selected node'
                  : 'Select a node to edit'
              : rightSidebarTab === 'variables'
                ? 'Define variables for this workflow'
                : selectedDeviceId
                  ? 'Real-time execution log from device'
                  : 'Select a device to see execution log'}
          </p>
        </div>
      )}

      {/* Tab Content */}
      {rightSidebarTab === 'properties' && <WorkflowPropertiesTab />}
      {rightSidebarTab === 'variables' && <WorkflowVariablesTab />}
      {rightSidebarTab === 'execution' && <WorkflowExecutionTab />}
      {rightSidebarTab === 'modification-log' && <WorkflowModificationLogTab />}
      {rightSidebarTab === 'ai-chat' && <WorkflowAIChatTab />}
    </div>
  );
};
